import { useState } from 'react'
import { Button, FormHelperText, IconButton, Stack } from '@mui/material'
import { AddCircleOutlineRounded, DeleteOutline } from '@mui/icons-material'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * The filter rules of ONE job notice type ("inicio de job" / "fin de job").
 *
 * RN15 — these are the ONLY two scopes that admit a filter. Everything else (every group and every
 * other type) stays a single toggle per row, with no add action.
 *
 * D18 — the filter belongs to the RULE, not to the type: the same subject may hold several rules of
 * the same type with different filters, so this lists them and can add and remove them one by one.
 *
 * RN16 — each dimension is a list of wanted values: empty dimension = no restriction on that
 * dimension, OR inside a dimension and AND between dimensions. The semantics live in Sun; what the
 * dialog owes the administrator is saying out loud that empty means all.
 */

// The three dimensions of CA20, in the order the scenario names them.
const DIMENSIONS = [
  { key: 'clients', label: 'Clients' },
  { key: 'technologies', label: 'Technologies' },
  { key: 'devices', label: 'Devices' }
]

// A rule travels down with its values as `{id, name}` objects and travels up as plain ids.
export const filterIdsOf = (rule) => ({
  clients: (rule.clients || []).map((item) => item.id),
  technologies: (rule.technologies || []).map((item) => item.id),
  devices: (rule.devices || []).map((item) => item.id)
})

// R2/S151: the identity of a filterable rule is `(sujeto, tipo, filtro)`, and the filter is compared
// BY SET, not by order — picking clients A then B is the same rule as picking B then A.
const sameSet = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false
  }

  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()

  return sortedLeft.every((value, index) => value === sortedRight[index])
}

export const sameFilter = (left, right) =>
  sameSet(left.clients, right.clients) &&
  sameSet(left.technologies, right.technologies) &&
  sameSet(left.devices, right.devices)

/**
 * Whether `filter` already exists among `rules`. `exceptRuleId` is the rule being edited, which must
 * not count as a duplicate of itself.
 */
export const isDuplicateFilter = (filter, rules, exceptRuleId = 0) =>
  (rules || []).some((rule) => rule.id !== exceptRuleId && sameFilter(filter, filterIdsOf(rule)))

const AlarmSubscriptionJobFilter = ({ typeCode, rules, options, onAddRule, onChangeRule, onRemoveRule }) => {
  const { t_ } = useSmartTranslation()
  const [duplicate, setDuplicate] = useState(false)

  const changeDimension = (rule, dimensionKey, selected) => {
    const candidate = {
      ...filterIdsOf(rule),
      [dimensionKey]: (selected || []).map((item) => item.id)
    }

    // S151: the dialog does not create — nor keep on screen — a rule the server would collapse into
    // an existing one.
    if (isDuplicateFilter(candidate, rules, rule.id)) {
      setDuplicate(true)
      return
    }

    setDuplicate(false)
    onChangeRule(rule.id, candidate)
  }

  // A new rule is born with the empty filter, which means all jobs (RN16), and the administrator sets
  // its filter afterwards (S148). So adding is NEVER blocked here: blocking it would make a second
  // rule impossible to create, since every rule starts out empty. The duplicate is caught when the
  // filter is actually SET, which is what S151 describes; and if the server collapses a repeated add
  // idempotently (R2), the dialog simply keeps whatever state the server returns.
  const addRule = () => {
    setDuplicate(false)
    onAddRule()
  }

  return (
    <Stack spacing={1}>
      {(rules || []).map((rule) => (
        <Stack
          key={rule.id}
          direction='row'
          spacing={1}
          alignItems='center'
          data-testid={'job-rule-' + typeCode + '-' + rule.id}
        >
          {DIMENSIONS.map((dimension) => (
            <SmartSelect
              key={dimension.key}
              multiple
              size='small'
              label={t_(dimension.label)}
              helperText={t_('Empty means all')}
              selectableOptions={options[dimension.key] || []}
              value={rule[dimension.key] || []}
              onChange={(selected) => changeDimension(rule, dimension.key, selected)}
            />
          ))}
          <IconButton
            aria-label={'job-rule-remove-' + typeCode + '-' + rule.id}
            title={t_('Remove rule')}
            onClick={() => onRemoveRule(rule.id)}
          >
            <DeleteOutline fontSize='small' />
          </IconButton>
        </Stack>
      ))}
      <Button
        aria-label={'job-rule-add-' + typeCode}
        startIcon={<AddCircleOutlineRounded />}
        onClick={addRule}
        size='small'
      >
        {t_('Add rule')}
      </Button>
      {duplicate
        ? <FormHelperText error>{t_('A rule with this filter already exists')}</FormHelperText>
        : null}
    </Stack>
  )
}

export default AlarmSubscriptionJobFilter
